/**
 * Typed TanStack Query keys.  Keep all keys flowing through this factory so
 * invalidation stays consistent across files (e.g. after creating a watchlist
 * we invalidate `keys.watchlists.all`).
 */
export const keys = {
  auth: {
    me: ['auth', 'me'] as const,
    sessions: ['auth', 'sessions'] as const,
  },
  announcements: ['announcements'] as const,
  leaderboard: (q: object) => ['leaderboard', q] as const,
  wallets: {
    list: (q: object) => ['wallets', 'list', q] as const,
    detail: (address: string) => ['wallets', 'detail', address] as const,
    trades: (address: string, q: object) =>
      ['wallets', 'trades', address, q] as const,
    metrics: (address: string, kind: string) => ['wallets', 'metrics', address, kind] as const,
    activity: (address: string, q: object) =>
      ['wallets', 'activity', address, q] as const,
  },
  walletTraces: {
    list: ['wallet-traces', 'list'] as const,
    detail: (address: string) => ['wallet-traces', 'detail', address.toLowerCase()] as const,
    status: (address: string) => ['wallet-traces', 'status', address.toLowerCase()] as const,
    records: (address: string, connIdx: number, q: object) =>
      ['wallet-traces', 'records', address.toLowerCase(), connIdx, q] as const,
    adminRequests: ['wallet-traces', 'admin-requests'] as const,
  },
  markets: {
    detail: (cid: string) => ['markets', 'detail', cid] as const,
    prices: (cid: string, q: object) => ['markets', 'prices', cid, q] as const,
    moves: (cid: string, q: object) => ['markets', 'moves', cid, q] as const,
    trades: (cid: string, q: object) =>
      ['markets', 'trades', cid, q] as const,
    topTraders: (cid: string, q: object) =>
      ['markets', 'top-traders', cid, q] as const,
    eventContext: (cid: string) => ['markets', 'event-context', cid] as const,
    eventTopTraders: (cid: string, q: object) =>
      ['markets', 'event-top-traders', cid, q] as const,
    holders: (cid: string) => ['markets', 'holders', cid] as const,
  },
  tags: (q: object) => ['tags', q] as const,
  savedSearches: {
    all: ['saved-searches'] as const,
    list: (screen: string | null) => ['saved-searches', screen] as const,
  },
  watchlists: {
    all: ['watchlists'] as const,
    detail: (id: number) => ['watchlists', id] as const,
    wallets: (id: number) => ['watchlists', id, 'wallets'] as const,
    rules: (id: number) => ['watchlists', id, 'rules'] as const,
  },
  admin: {
    users: (q: object) => ['admin', 'users', q] as const,
    user: (id: number) => ['admin', 'users', id] as const,
    sessions: (id: number) => ['admin', 'users', id, 'sessions'] as const,
    domains: ['admin', 'domains'] as const,
    tags: (q: object) => ['admin', 'tags', q] as const,
    monitoredKeywords: ['admin', 'monitored-keywords'] as const,
    keywordBackfillStatus: ['admin', 'monitored-keywords', 'backfill-status'] as const,
    config: ['admin', 'config'] as const,
    announcements: ['admin', 'announcements'] as const,
    savedViews: ['admin', 'saved-views'] as const,
    watchlists: ['admin', 'watchlists'] as const,
    monitoring: {
      overview: ['admin', 'monitoring', 'overview'] as const,
      activity: (q: object) => ['admin', 'monitoring', 'activity', q] as const,
      userActivity: (id: number) => ['admin', 'monitoring', 'user-activity', id] as const,
      whoViewed: (q: object) =>
        ['admin', 'monitoring', 'who-viewed', q] as const,
      audit: (q: object) => ['admin', 'monitoring', 'audit', q] as const,
    },
    system: {
      jobs: ['admin', 'system', 'jobs'] as const,
      stats: ['admin', 'system', 'stats'] as const,
    },
  },
} as const;
